import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Box,
    Button,
    Flex,
    Heading,
    HStack,
    IconButton,
    Image,
    Spinner,
    Stack,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useDisclosure,
    VStack,
} from "@chakra-ui/react";
import {AddIcon, DeleteIcon, EditIcon, ViewIcon} from "@chakra-ui/icons";
import * as React from "react";
import {useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useMutation, useInfiniteQuery} from "@tanstack/react-query";
import {deleteLotNew, getLotsMineNew, getLotsNew} from "../../../../data/api/Customer";
import {LotModelNew} from "../../../../data/model/Lot";
import {
    getConsumeWithin,
    getFormattedDataFromTimestamp,
    getQrRandomHash,
    priceNumber,
} from "../../../../utils/Utils";
import {CustomModal} from "../../../../components/molecules/ModalComponent/ModalComponent";
import {QrIcon} from "../../../../components/atoms/SvgImage";
import QRCode from "qrcode.react";
import {AddTableRow, KeyValueRow} from "../../../../components/atoms/AddTableRow/AddTableRow";
import {ColorsClass} from "../../../../theme/colors";

interface LotsResponse {
    isLastPage: boolean;
    totPage: number;
    currentPage: number;
    data: LotModelNew[];
}

export default function LotsPage() {
    const navigate = useNavigate();
    const userType = localStorage.getItem("userType");

    const {
        data,
        isLoading,
        isError,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery<LotsResponse, Error>({
        queryKey: ["lots"],
        initialPageParam: 0,
        queryFn: async ({pageParam = 0}) => {
            const res =
                userType === "seller"
                    ? await getLotsMineNew({page: pageParam as number})
                    : await getLotsNew({page: pageParam as number});
            return res?.data?.data as LotsResponse;
        },
        getNextPageParam: (lastPage) =>
            lastPage?.isLastPage ? undefined : lastPage?.currentPage + 1,
    });

    const lots: LotModelNew[] = data?.pages?.flatMap((page) => page?.data) ?? [];

    return (
        <Box p={6}>
            <Flex justify="space-between" align="center" mb={4}>
                <Heading size="lg">
                    {userType === "seller" ? "I miei lotti" : "Tutti i lotti"}
                </Heading>
                {userType === "seller" && (
                    <Button
                        leftIcon={<AddIcon/>}
                        colorScheme="teal"
                        onClick={() => navigate("/products/lots/add")}
                    >
                        Aggiungi
                    </Button>
                )}
            </Flex>

            {isLoading && (
                <Flex justify="center" py={10}>
                    <Spinner size="xl"/>
                </Flex>
            )}

            {isError && (
                <Text color="red.500" textAlign="center">
                    Errore nel caricamento
                </Text>
            )}

            {!isLoading && (
                <TableContainer overflowX="auto" w="100%">
                    <Table variant="simple" pb="100px" minW="900px">
                        <Thead>
                            <Tr>
                                <Th>Immagine</Th>
                                <Th>COD. Lotto</Th>
                                <Th>Prodotto</Th>
                                <Th>Qta</Th>
                                <Th>Scadenza</Th>
                                <Th>Consumare entro</Th>
                                <Th>Prodotto il</Th>
                                <Th minW="200px" whiteSpace="nowrap">
                                    Azioni
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody pb="100px">
                            {lots?.map((lot, index) => (
                                <LotRow key={`${lot?.id}_${index}`} lot={lot} />
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
            )}

            <Flex justify="center" mt={4}>
                {(hasNextPage) ? (
                    <Button onClick={() => fetchNextPage()} isLoading={isFetchingNextPage}>
                        Carica altro
                    </Button>
                ) : (
                    <Text color="gray.500">Nessun altro lotto</Text>
                )}
            </Flex>
        </Box>
    );
}

export function LotRow({lot}: { lot: LotModelNew }) {
    const navigate = useNavigate();
    const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

    const {isOpen, onOpen, onClose} = useDisclosure();
    const {
        isOpen: isOpenQr,
        onOpen: onOpenQr,
        onClose: onCloseQr,
    } = useDisclosure();

    const [exposed, setExposed] = useState(false);

    console.log({lot});

    const {
        id,
        product,
        variant,
        quantity,
        price,
        discountPrice,
        expiration,
        consumeWithin,
        producedOn,
        code,
    } = lot || {};
    const {id: productId, mediaList, name} = product || {};
    const {id: variantId, key: variantKey, value: variantValue} = variant || {};

    const storageId = `${id?.substring(0, 5)}_${getQrRandomHash()}`;

    const nfcLink = `enjoyetna://main?id=${id}&storageId=${id?.substring(0, 10)}_${storageId}`;
    const nfcLinkExposed = `enjoyetna://main?id=${id}`;
    const qrLink = `https://www.enjoyetna.com?id=${id}&storageId=${id?.substring(0, 10)}_${storageId}`;
    const qrLinkExposed = `https://www.enjoyetna.com?id=${id}`;

    const deleteMutation = useMutation({
        mutationFn: (id: string) => deleteLotNew(id),
        onSuccess: () => window.location.reload(),
    });

    const image = lot?.product?.mediaList?.find((m) => m.isMain) || {};

    return (
        <>
            <Tr>
                <Td>
                    <Image
                        boxSize="64px"
                        borderRadius="16px"
                        objectFit="cover"
                        src={image?.url}
                        alt={lot?.product?.name}
                    />
                </Td>
                <Td>{code}</Td>
                <Td>{lot?.product?.name}</Td>
                <Td>{quantity}</Td>
                <Td>{getFormattedDataFromTimestamp(expiration) || "-"}</Td>
                <Td>{getConsumeWithin(consumeWithin) || "-"}</Td>
                <Td>{getFormattedDataFromTimestamp(producedOn) || "-"}</Td>
                <Td whiteSpace="nowrap">
                    <HStack spacing={2}>
                        <IconButton
                            aria-label="Mostra QR"
                            icon={<QrIcon w={"16"} h={"16"}/>}
                            size="lg"
                            onClick={onOpenQr}
                        />
                        <IconButton
                            aria-label="Dettaglio"
                            icon={<ViewIcon/>}
                            size="lg"
                            onClick={onOpen}
                        />
                        <IconButton
                            aria-label="Modifica"
                            icon={<EditIcon/>}
                            size="lg"
                            onClick={() => navigate(`/products/lots/edit/${id}`)}
                        />
                        <IconButton
                            aria-label="Elimina"
                            icon={<DeleteIcon/>}
                            size="lg"
                            colorScheme="red"
                            onClick={() => setIsConfirmDeleteOpen(true)}
                        />
                    </HStack>
                </Td>
            </Tr>

            {/* Modal QR */}
            <CustomModal
                isOpen={isOpenQr}
                title={name || "Lotto"}
                onClose={onCloseQr}
                content={
                    <VStack p={"48px"} alignItems={"center"} overflow={"auto"} justifyContent={"center"}>
                        <QRCode
                            style={{display: "none"}}
                            id={"qrCodeContainer"}
                            size={2000}
                            value={
                                exposed
                                    ? `https://www.enjoyetna.com?id=${id}`
                                    : `https://www.enjoyetna.com?id=${id}&storageId=${id?.substring(0, 10)}_${storageId}`
                            }
                        />

                        <QRCode
                            id={"qrCodeContainer"}
                            size={256}
                            value={
                                exposed
                                    ? `https://www.enjoyetna.com?id=${id}`
                                    : `https://www.enjoyetna.com?id=${id}&storageId=${id?.substring(0, 10)}_${storageId}`
                            }
                        />

                        <Button
                            mt={12}
                            onClick={() => setExposed(!exposed)}
                            color={exposed ? "white" : "black"}
                            backgroundColor={exposed ? ColorsClass.chatbotBlue : undefined}
                        >
                            In esposizione
                        </Button>

                        <Text fontWeight={"bold"} mt={"24px"} color={"primary.400"}>
                            Link NFC:{" "}
                            <Text color={"black"} fontWeight={"regular"}>
                                {exposed ? nfcLinkExposed : nfcLink}
                            </Text>
                        </Text>

                        <Text fontWeight={"bold"} color={"primary.400"}>
                            Link QR:{" "}
                            <Text color={"black"} fontWeight={"regular"}>
                                {exposed ? qrLinkExposed : qrLink}
                            </Text>
                        </Text>

                        <HStack>
                            <Button mt={"24px"} color={"primary.400"} onClick={() => downloadQRCode(storageId)}>
                                Scarica QR
                            </Button>
                            <Button
                                mt={"24px"}
                                color={"primary.400"}
                                onClick={() => {
                                    navigator.clipboard.writeText(exposed ? nfcLinkExposed : nfcLink).then(() => {
                                        alert("Copiato negli appunti!");
                                    });
                                }}
                            >
                                Copia NFC
                            </Button>
                            <Button
                                mt={"24px"}
                                color={"primary.400"}
                                onClick={() => {
                                    navigator.clipboard.writeText(exposed ? qrLinkExposed : qrLink).then(() => {
                                        alert("Copiato negli appunti!");
                                    });
                                }}
                            >
                                Copia Link QR
                            </Button>
                        </HStack>
                    </VStack>
                }
            />

            {/* Modal Dettaglio */}
            <CustomModal
                isOpen={isOpen}
                onClose={onClose}
                title={name || "Lotto"}
                content={
                    <HStack>
                        <Stack
                            h={"240px"}
                            minH={"240px"}
                            backgroundColor={"white"}
                            borderWidth={"1px"}
                            borderColor={"white"}
                            borderRadius={"16px"}
                            overflow={"hidden"}
                        >
                            <Image h={"240px"} src={image.url}/>
                        </Stack>
                        <TableContainer py={"16px"}>
                            <Table variant={"none"}>
                                <Tbody>
                                    {id && <AddTableRow name={"ID"}>
                                        <Text>{id}</Text>
                                    </AddTableRow>}
                                    <AddTableRow name={"Quantità"}>
                                        <Text>{quantity}</Text>
                                    </AddTableRow>
                                    {price && (
                                        <AddTableRow name={"Prezzo"}>
                                            <Text>{priceNumber(price)}</Text>
                                        </AddTableRow>
                                    )}
                                    {discountPrice && (
                                        <AddTableRow name={"Prezzo scontato"}>
                                            <Text>{priceNumber(discountPrice)}</Text>
                                        </AddTableRow>
                                    )}
                                    <AddTableRow name={"Prodotto"}>
                                        <VStack alignItems={"flex-start"}>
                                            <KeyValueRow name={"ID"}>{productId}</KeyValueRow>
                                            <KeyValueRow name={"Nome"}>{product?.name}</KeyValueRow>
                                        </VStack>
                                    </AddTableRow>
                                    {variantId && (
                                        <AddTableRow name={"Variante"}>
                                            <VStack alignItems={"flex-start"}>
                                                <KeyValueRow name={"ID"}>{variantId}</KeyValueRow>
                                                <KeyValueRow name={variantKey || "-"}>{variantValue}</KeyValueRow>
                                            </VStack>
                                        </AddTableRow>
                                    )}
                                    <AddTableRow name={"Scadenza"}>
                                        <Text>{getFormattedDataFromTimestamp(expiration) || "-"}</Text>
                                    </AddTableRow>
                                    <AddTableRow name={"Consumare entro"}>
                                        <Text>{getConsumeWithin(consumeWithin) || "-"}</Text>
                                    </AddTableRow>
                                    <AddTableRow name={"Prodotto il"}>
                                        <Text>{getFormattedDataFromTimestamp(producedOn) || "-"}</Text>
                                    </AddTableRow>
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </HStack>
                }
            />

            {/* Dialog conferma eliminazione */}
            <ConfirmDeleteDialog
                isOpen={isConfirmDeleteOpen}
                onClose={() => setIsConfirmDeleteOpen(false)}
                onConfirm={() => deleteMutation.mutate(lot?.id || "")}
            />
            <HStack cursor={"pointer"} h={"64px"} px={"24px"} borderRadius={"24px"} position={"absolute"}
                    alignItems={"center"}
                    justifyContent={"center"} bottom={"16px"} right={"16px"} backgroundColor={"secondary.200"}
                    _hover={{backgroundColor: "tertiary.200"}} onClick={() => {
                navigate("/products/lots/add");
            }}>
                <AddIcon w={"16px"} h={"16px"} alignSelf={"center"}/>
                <Text ms={"8px"} fontWeight={"bold"}>Aggiungi</Text>
            </HStack>
        </>
    );
}

function downloadQRCode(storageId: string) {
    const qrCodeURL = document?.querySelector("canvas")?.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = qrCodeURL || "";
    link.download = `${storageId}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

/*
import {
    AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
    Box, Button,
    HStack,
    Image,
    Stack,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Tr,
    useDisclosure,
    VStack
} from "@chakra-ui/react";
import {scrollbarCss} from "../../../../theme/theme";
import {TableComponent} from "../Products/ProductsPage";
import {
    CategoriesTableBodyLoading
} from "../../../../components/organisms/CategoriesTableComponent/CategoriesTableComponent";
import {AddIcon, DeleteIcon, EditIcon} from "@chakra-ui/icons";
import * as React from "react";
import {useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useMutation, useQuery} from "@tanstack/react-query";
import {deleteLotNew, getLotsMineNew, getLotsNew} from "../../../../data/api/Customer";
import {TableIcon} from "../../../../components/atoms/TableIcon/TableIcon";
import {EyeIcon, QrIcon} from "../../../../components/atoms/SvgImage";
import {LotModelNew} from "../../../../data/model/Lot";
import {
    getConsumeWithin,
    getFormattedDataFromTimestamp,
    getQrRandomHash,
    priceNumber,
    usePaginationData
} from "../../../../utils/Utils";
import {CustomModal} from "../../../../components/molecules/ModalComponent/ModalComponent";
import {AddTableRow, KeyValueRow} from "../../../../components/atoms/AddTableRow/AddTableRow";
import {
    PaginationInputComponent
} from "../../../../components/molecules/PaginationInputComponent/PaginationInputComponent";
import {QRCodeCanvas as QRCode} from 'qrcode.react';
import {ColorsClass} from "../../../../theme/colors";

export default function LotsPage() {
    const navigate = useNavigate();
    const userType = localStorage.getItem("userType");
    const [page, setPage] = useState(1);

    // TODO: filtraggio
    const responseAll = useQuery({
        queryKey: ['get-mine-lots', page],
        queryFn: () => userType === "seller" ? getLotsMineNew({page: page - 1}) : getLotsNew({page: page - 1}),
        refetchOnWindowFocus: "always"
    });

    const {
        data: lots,
        isLoading,
        totPage,
        refetch
    } = usePaginationData<LotModelNew[]>(responseAll);

    return (
        <VStack w={"100%"} alignItems={"flex-start"} overflow={"auto"}
                css={scrollbarCss({marginLeft: 0, marginRight: 0})}>

            <HStack pt={"24px"}>
                <Text fontSize={"24px"} fontWeight={"bold"}
                      ms={"24px"}>{userType === "seller" ? "I miei lotti" : "Tutti i lotti"}</Text>
                <PaginationInputComponent setPage={setPage} page={page} totPage={totPage}/>
            </HStack>

            <Box mt={"18px"} mx={"24px"} alignSelf={"stretch"} backgroundColor={"surfaceContainer.light"}
                 borderRadius={"16px"} overflowX={"hidden"} mb={"72px"}
                 css={scrollbarCss({marginLeft: 0, marginRight: 0})}>
                <TableComponent body={isLoading ? <CategoriesTableBodyLoading isAllCategories={true}/> :
                    <LotTableBody data={lots || []} refetch={refetch}
                                  baseKey={'all_categories_row_index'}/>}
                                headers={["", "ID", "Prodotto", "Qta", "Scadenza", "Consumare entro", "Prodotto il", ""]}/>
            </Box>

            <HStack cursor={"pointer"} h={"64px"} px={"24px"} borderRadius={"24px"} position={"absolute"}
                    alignItems={"center"}
                    justifyContent={"center"} bottom={"16px"} right={"16px"} backgroundColor={"secondary.200"}
                    _hover={{backgroundColor: "tertiary.200"}} onClick={() => {
                navigate("/products/lots/add");
            }}>
                <AddIcon w={"16px"} h={"16px"} alignSelf={"center"}/>
                <Text ms={"8px"} fontWeight={"bold"}>Aggiungi</Text>
            </HStack>
        </VStack>
    );
}

const LotTableBody = ({data, baseKey, refetch}: {
    data?: LotModelNew[],
    baseKey?: string,
    refetch: () => void
}) => {
    return <Tbody>
        {(data?.length ?? 0) > 0 && data?.map((value, index) => {
            return <LotTableComponent key={`${baseKey}_${index}`} model={value} refetch={refetch}/>;
        })}
    </Tbody>
}

export const LotTableComponent = ({model, refetch}: { model: LotModelNew, refetch: () => void }) => {
    const {id, product, variant, quantity, price, discountPrice, expiration, consumeWithin, producedOn} = model || {};
    const {id: productId, mediaList, name} = product || {};
    const {id: variantId, key: variantKey, value: variantValue} = variant || {};
    const image = mediaList?.filter((value) => value.isMain === true)[0] || {};
    const {isOpen, onOpen, onClose} = useDisclosure();
    const {isOpen: isOpenQr, onOpen: onOpenQr, onClose: onCloseQr} = useDisclosure();
    const navigate = useNavigate();
    const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

    const {mutateAsync} = useMutation({
        mutationKey: ["delete-product-lot"],
        mutationFn: (id: string) => deleteLotNew(id)
    });

    const [exposed, setExposed] = useState(false);

    const storageId = `${id?.substring(0, 5)}_${getQrRandomHash()}`;

    function downloadQRCode() {
        const qrCodeURL = document?.querySelector('canvas')?.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = qrCodeURL || '';
        link.download = `${storageId}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    // const downloadQRCodeSVG = (storageId: string) => {
    //     const svgElement = document?.querySelector('#storageQrContainer svg');
    //
    //     if (!svgElement) {
    //         console.error("SVG element not found");
    //         return;
    //     }
    //
    //     const svgData = new XMLSerializer().serializeToString(svgElement);
    //     const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    //     const svgUrl = URL.createObjectURL(svgBlob);
    //
    //     const link = document.createElement('a');
    //     link.href = svgUrl;
    //     link.download = `${storageId}.svg`;
    //
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    // };

    const nfcLink = `enjoyetna://main?id=${id}&storageId=${id?.substring(0, 10)}_${storageId}`;
    const nfcLinkExposed = `enjoyetna://main?id=${id}`;
    const qrLink = `https://www.enjoyetna.com?id=${id}&storageId=${id?.substring(0, 10)}_${storageId}`;
    const qrLinkExposed = `https://www.enjoyetna.com?id=${id}`;

    return <>
        <Tr>
            <Td>
                <Stack w={"64px"} h={"64px"} minW={"64px"} minH={"64px"} backgroundColor={"white"}
                       borderWidth={"1px"} borderColor={"white"} borderRadius={"16px"} overflow={"hidden"}>
                    <Image w={"64px"} h={"64px"} minW={"64px"} minH={"64px"} aspectRatio={1} objectFit={'contain'}
                           backgroundColor={'white'} src={image.url}/>
                </Stack>
            </Td>
            <Td>{id || "-"}</Td>
            <Td>{`${name}${variantKey ? ` - ${variantKey}: ${variantValue}` : ""}` || "-"}</Td>
            <Td>{quantity || "-"}</Td>
            <Td>{getFormattedDataFromTimestamp(expiration) || "-"}</Td>
            <Td>{getConsumeWithin(consumeWithin) || "-"}</Td>
            <Td>{getFormattedDataFromTimestamp(producedOn) || "-"}</Td>
            <Td>
                <HStack spacing={"16px"}>
                    <TableIcon icon={<QrIcon/>} onClick={onOpenQr}/>
                    <TableIcon icon={<EyeIcon/>} onClick={onOpen}/>
                    <TableIcon icon={<EditIcon/>} onClick={() => navigate(`/products/lots/edit/${id}`)}/>
                    <TableIcon icon={<DeleteIcon/>} onClick={() => {
                        setIsConfirmDeleteOpen(true);
                    }}/>
                </HStack>
            </Td>
        </Tr>
        <CustomModal isOpen={isOpenQr} title={name || "Lotto"} onClose={onCloseQr} content={
            <VStack p={"48px"} alignItems={'center'} overflow={'auto'} justifyContent={'center'}>
                <QRCode style={{display: 'none'}} id={'qrCodeContainer'} size={2000}
                        value={exposed ? `https://www.enjoyetna.com?id=${id}` : `https://www.enjoyetna.com?id=${id}&storageId=${id?.substring(0, 10)}_${storageId}`}/>

                <QRCode id={'qrCodeContainer'} size={256}
                        value={exposed ? `https://www.enjoyetna.com?id=${id}` : `https://www.enjoyetna.com?id=${id}&storageId=${id?.substring(0, 10)}_${storageId}`}/>

                <Button mt={12} onClick={() => setExposed(!exposed)}
                        color={exposed ? 'white' : 'black'}
                        backgroundColor={exposed ? ColorsClass.chatbotBlue : undefined}>In esposizione</Button>

                <Text fontWeight={'bold'} mt={'24px'} color={'primary.400'}>Link NFC: <Text color={'black'}
                                                                                            fontWeight={'regular'}>{exposed ? nfcLinkExposed : nfcLink}</Text></Text>

                <Text fontWeight={'bold'} color={'primary.400'}>Link QR: <Text color={'black'}
                                                                               fontWeight={'regular'}>{exposed ? qrLinkExposed : qrLink}</Text></Text>

                <HStack>
                    <Button mt={"24px"} color={'primary.400'} onClick={() => downloadQRCode()}>Scarica QR</Button>
                    <Button mt={"24px"} color={'primary.400'} onClick={() => {
                        navigator.clipboard.writeText(exposed ? nfcLinkExposed : nfcLink).then(_ => {
                            alert('Copiato negli appunti!');
                        });
                    }}>Copia NFC</Button>
                    <Button mt={"24px"} color={'primary.400'} onClick={() => {
                        navigator.clipboard.writeText(exposed ? qrLinkExposed : qrLink).then(_ => {
                            alert('Copiato negli appunti!');
                        });
                    }}>Copia Link QR</Button>
                </HStack>
            </VStack>}/>
        <CustomModal isOpen={isOpen} onClose={onClose} title={name || "Lotto"} content={<HStack>
            <Stack h={"240px"} minH={"240px"} backgroundColor={"white"}
                   borderWidth={"1px"} borderColor={"white"} borderRadius={"16px"} overflow={"hidden"}>
                <Image h={"240px"} src={image.url}/>
            </Stack>
            <TableContainer py={"16px"}>
                <Table variant={'none'}>
                    <Tbody>
                        <AddTableRow name={"ID"}><Text>{id}</Text></AddTableRow>
                        <AddTableRow name={"Quantità"}><Text>{quantity}</Text></AddTableRow>
                        {price && <AddTableRow name={"Prezzo"}><Text>{priceNumber(price)}</Text></AddTableRow>}
                        {discountPrice &&
                            <AddTableRow
                                name={"Prezzo scontato"}><Text>{priceNumber(discountPrice)}</Text></AddTableRow>}
                        <AddTableRow name={"Prodotto"}>
                            <VStack alignItems={"flex-start"}>
                                <KeyValueRow name={"ID"}>{productId}</KeyValueRow>
                                <KeyValueRow name={"Nome"}>{product?.name}</KeyValueRow>
                            </VStack>
                        </AddTableRow>
                        {variantId && <AddTableRow name={"Variante"}>
                            <VStack alignItems={"flex-start"}>
                                <KeyValueRow name={"ID"}>{variantId}</KeyValueRow>
                                <KeyValueRow name={variantKey || "-"}>{variantValue}</KeyValueRow>
                            </VStack>
                        </AddTableRow>}
                        <AddTableRow
                            name={"Scadenza"}><Text>{getFormattedDataFromTimestamp(expiration) || "-"}</Text></AddTableRow>
                        <AddTableRow
                            name={"Consumare entro"}><Text>{getConsumeWithin(consumeWithin) || "-"}</Text></AddTableRow>
                        <AddTableRow
                            name={"Prodotto il"}><Text>{getFormattedDataFromTimestamp(producedOn) || "-"}</Text></AddTableRow>
                    </Tbody>
                </Table>
            </TableContainer>
        </HStack>}/>
        <ConfirmDeleteDialog isOpen={isConfirmDeleteOpen} onConfirm={() => mutateAsync(id || '').then(refetch)}
                             onClose={() => setIsConfirmDeleteOpen(false)}/>
    </>
}

type ConfirmDeleteDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
};

export const ConfirmDeleteDialog: React.FC<ConfirmDeleteDialogProps> = ({isOpen, onClose, onConfirm}) => {
    const cancelRef = useRef<HTMLButtonElement>(null);

    return (
        <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={onClose}
        >
            <AlertDialogOverlay>
                <AlertDialogContent>
                    <AlertDialogHeader>Conferma Eliminazione</AlertDialogHeader>
                    <AlertDialogBody>
                        Sei sicuro di voler eliminare questo elemento?
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelRef} onClick={onClose}>
                            Annulla
                        </Button>
                        <Button colorScheme="red" onClick={onConfirm} ml={3}>
                            Elimina
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
};
*/

type ConfirmDeleteDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
};


export const ConfirmDeleteDialog: React.FC<ConfirmDeleteDialogProps> = ({isOpen, onClose, onConfirm}) => {
    const cancelRef = useRef<HTMLButtonElement>(null);

    return (
        <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={onClose}
        >
            <AlertDialogOverlay>
                <AlertDialogContent>
                    <AlertDialogHeader>Conferma Eliminazione</AlertDialogHeader>
                    <AlertDialogBody>
                        Sei sicuro di voler eliminare questo elemento?
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelRef} onClick={onClose}>
                            Annulla
                        </Button>
                        <Button colorScheme="red" onClick={onConfirm} ml={3}>
                            Elimina
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
};
